<template>
	<div>
		<StandardModal
			open
			:title="$t('groupinvites_new_title')"
			:confirmText="$t('groupinvites_invite_create_label')"
			:confirmProps="{ loading: isCreating }"
			@cancel="onCancel"
			@confirm="onSubmit"
			ref="modal"
		>
			<div class="content group-invite-creation">
				<div
					class="heading"
					v-text="
						$t('groupinvites_invite_create_desc', {
							Count: org.bubble_membership_cap,
						})
					"
				/>
				<div class="action-row" @click="showExpirationPicker = true">
					<div class="action-row-left">
						<CalendarClockIcon type="regular" />
						{{ $t('groupinvites_expire_date') }}
					</div>
					<div class="action-row-right">
						{{ selectedExpirationLabel }}
						<ChevronIcon right />
					</div>
				</div>
				<div
					class="action-row"
					:class="{ disabled: !canCreateExternal }"
					@click="toggleAccountCreation"
				>
					<div class="action-row-left">
						<GlobeIcon type="regular" />
						{{ $t('groupinvites_invite_acct_creation_action') }}
					</div>
					<div class="action-row-right">
						<ToggleSwitch
							class="allow-creation-toggle"
							v-model="allowCreation"
							:disabled="!canCreateExternal"
							@click.stop
						/>
					</div>
				</div>
				<div
					class="action-row-desc"
					v-t="
						canCreateExternal
							? 'groupinvites_invite_acct_creation_desc'
							: 'groupinvites_invite_acct_creation_disabled_label'
					"
				/>
			</div>
		</StandardModal>

		<!-- EXPIRATION OPTIONS MODAL -->
		<SimpleModal
			v-if="showExpirationPicker"
			@close="showExpirationPicker = false"
		>
			<div class="expiration-date-picker">
				<h2 v-t="'groupinvites_expire_date'" />
				<div
					class="expiration-date-option"
					v-for="opt in expirationOptions"
					:key="opt.hoursOffset"
					v-text="$t(opt.label)"
					@click="setExpiration(opt)"
				/>
			</div>
		</SimpleModal>

		<!-- CUSTOM EXPIRATION MODAL -->
		<SimpleModal
			v-if="showCalendarPicker"
			@close="closeDatePicker"
			class="custom-date-picker"
		>
			<div class="date-picker-container">
				<DatePicker v-model="expirationCustomDate" :min="minDate" />
			</div>
		</SimpleModal>
	</div>
</template>

<script>
import {
	StandardModal,
	SimpleModal,
	DatePicker,
	ToggleSwitch,
} from '@/components/common'
import { CalendarClockIcon, ChevronIcon, GlobeIcon } from '@/icons'
import { getOrganization } from '@/store'
import { post } from '@/helpers/api'
import { ADD_USER, orgCan } from '@/helpers/permissions'

export const accessTypes = {
	INTERNAL: 'internal',
	EXTERNAL: 'external',
}

const defaultExpiration = {
	label: 'groupinvites_expire_option_days_7',
	offsetValue: 7,
	offsetUnit: 'days',
}
const expirationOptions = [
	{
		label: 'groupinvites_expire_option_hours_1',
		offsetValue: 1,
		offsetUnit: 'hours',
	},
	{
		label: 'groupinvites_expire_option_hours_6',
		offsetValue: 6,
		offsetUnit: 'hours',
	},
	{
		label: 'groupinvites_expire_option_days_1',
		offsetValue: 1,
		offsetUnit: 'days',
	},
	defaultExpiration,
	{ label: 'groupinvites_expire_option_none', offsetValue: null },
	{ label: 'groupinvites_expire_custom', offsetValue: 'custom' },
]

export default {
	name: 'GroupInviteCreation',
	components: {
		StandardModal,
		CalendarClockIcon,
		ChevronIcon,
		GlobeIcon,
		SimpleModal,
		DatePicker,
		ToggleSwitch,
	},
	props: {
		group: Object,
	},
	data() {
		return {
			org: getOrganization(),
			isCreating: false,
			showExpirationPicker: false,
			showCalendarPicker: false,
			selectedExpirationOption: defaultExpiration,
			expirationCustomDate: null,
			allowCreation: false,
			expirationOptions,
		}
	},

	emits: ['success', 'cancel'],

	computed: {
		selectedExpirationLabel() {
			const { label, offsetValue } = this.selectedExpirationOption
			if (offsetValue === 'custom' && this.expirationCustomDate) {
				return this.$m(this.expirationCustomDate).format('l')
			}
			return this.$t(label)
		},
		minDate() {
			// earliest custom date should be tomorrow
			return this.$m().add(1, 'days').toDate()
		},
		canCreateExternal() {
			return orgCan(ADD_USER)
		},
		accessOptions() {
			return [
				{
					value: accessTypes.INTERNAL,
					label: this.org.name,
					disabled: !this.canCreateExternal,
				},
				this.canCreateExternal && {
					value: accessTypes.EXTERNAL,
					label: this.$t('groupinvites_access_external_label'),
				},
			].filter((x) => x)
		},
	},

	watch: {
		expirationCustomDate() {
			this.showExpirationPicker = false
			this.showCalendarPicker = false
		},
	},

	methods: {
		async onSubmit() {
			this.isCreating = true

			try {
				const { offsetValue, offsetUnit } = this.selectedExpirationOption
				let expires = this.$m().add(offsetValue, offsetUnit).toDate()
				if (!offsetValue) {
					expires = null
				} else if (offsetValue === 'custom') {
					expires = this.expirationCustomDate
				}
				const access = this.allowCreation
					? accessTypes.EXTERNAL
					: accessTypes.INTERNAL
				const { data } = await post(
					`/api/clients/groups/${this.group.id}/invites`,
					{
						access,
						expires,
					}
				)
				this.$alert.success(this.$t('groupinvites_invite_create_success'))
				this.$emit('success', data.data)
				this.onCancel()
			} catch (e) {
				console.error(e)
				this.$refs.modal.$alert.error()
			}
			this.isCreating = false
		},

		onCancel() {
			this.$emit('cancel')
		},

		setExpiration(option) {
			this.selectedExpirationOption = option
			if (option.offsetValue === 'custom') {
				this.showCalendarPicker = true
				return
			}
			this.showCalendarPicker = false
			this.showExpirationPicker = false
			this.expirationCustomDate = null
		},

		closeDatePicker() {
			this.selectedExpirationOption = defaultExpiration
			this.showCalendarPicker = false
		},

		toggleAccountCreation() {
			if (this.canCreateExternal) {
				this.allowCreation = !this.allowCreation
				return
			}
			this.allowCreation = false
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.content {
	padding: 24px 16px;
	.heading {
		margin: 8px auto 32px auto;
		text-align: center;
		max-width: 475px;
	}
	.external-help-msg {
		display: flex;
		.pronto-icon {
			margin: 2px 8px 0 0;
			color: var(--blue-300);
		}
	}
}

.action-row {
	@include flex-spread;
	cursor: pointer;
	height: 56px;
	font-size: 15px;
	color: var(--neutral-800);
	border: 1px solid var(--neutral-200);
	border-radius: 6px;
	padding: 0 16px;
	background: #fcfcfc;
	margin-bottom: 24px;
	&.disabled {
		cursor: not-allowed;
	}
	.action-row-left {
		@include flex;
		.pronto-icon {
			color: var(--neutral-600);
			margin-right: 8px;
			font-size: 24px;
		}
	}
	.action-row-right {
		@include flex;
		justify-content: flex-end;
		.pronto-icon {
			color: var(--neutral-600);
			margin-left: 8px;
			font-size: 13px;
		}
	}
}

.action-row-desc {
	font-size: 14px;
	color: var(--neutral-500);
	padding: 0 8px;
	margin-top: -16px;
}

.allow-creation-toggle {
	font-size: 28px;
}

.expiration-date-picker {
	width: 390px;
	text-align: center;
	> h2 {
		width: 100%;
		font-family: Avenir-Black;
		font-size: 20px;
		margin: 24px 0;
	}
	.expiration-date-option {
		cursor: pointer;
		padding: 14px 0;
		border-top: 1px solid var(--neutral-200);
		&:hover {
			background: $light-blue;
		}
	}
}

.custom-date-picker {
	:deep(.modal-close-btn) {
		top: 12px;
		right: 12px;
	}
	.date-picker-container {
		padding: 36px;
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.group-invite-creation {
		.action-row {
			background: var(--neutral-100);
		}
	}
}
</style>
